import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { triggerToast } from '@/components/Toast'
import { ERROR_CODES } from '@/utils/constants'

import { useCreateReportingEvent } from '../../../reporting-events/hooks'
import { ReportingEvent } from '../../../reporting-events/types'
import { MAX_REPORTING_EVENTS } from '../../constants'

interface UseEventSelectionProps {
  value: string[]
  onChange: (value: string[], events: ReportingEvent[]) => void
  initialSelectedEvents: ReportingEvent[]
  onError?: (hasError: boolean, message?: string) => void
}

/**
 * Custom hook for managing event selection and creation
 */
export const useEventSelection = ({
  value,
  onChange,
  initialSelectedEvents,
  onError,
}: UseEventSelectionProps) => {
  const { t } = useTranslation()
  const [selectedEvents, setSelectedEvents] = useState<ReportingEvent[]>([])
  const createMutation = useCreateReportingEvent()

  useEffect(() => {
    if (value.length > 0 && initialSelectedEvents.length > 0) {
      setSelectedEvents(initialSelectedEvents)
    } else if (value.length === 0) {
      setSelectedEvents([])
    }
  }, [value, initialSelectedEvents])

  const handleSelectEvent = useCallback(
    (event: ReportingEvent) => {
      if (!value.includes(event.id)) {
        if (value.length >= MAX_REPORTING_EVENTS) {
          return
        }
        const newValue = [...value, event.id]
        const newEvents = [...selectedEvents, event]
        setSelectedEvents(newEvents)
        onChange(newValue, newEvents)
      }
    },
    [value, selectedEvents, onChange]
  )

  const handleRemoveEvent = useCallback(
    (eventId: string) => {
      const newValue = value.filter((id) => id !== eventId)
      const newEvents = selectedEvents.filter((e) => e.id !== eventId)
      setSelectedEvents(newEvents)
      onChange(newValue, newEvents)
      if (onError && newValue.length < MAX_REPORTING_EVENTS) {
        onError(false)
      }
    },
    [value, selectedEvents, onChange, onError]
  )

  const handleCreateAndSelect = useCallback(
    async (name: string) => {
      if (value.length >= MAX_REPORTING_EVENTS) {
        return
      }

      try {
        const response = await createMutation.mutateAsync({
          reporting_event_name: name,
        })

        if (response.data && response.data.length > 0) {
          const newEvent = response.data[0]
          const newValue = [...value, newEvent.id]
          const newEvents = [...selectedEvents, newEvent]
          setSelectedEvents(newEvents)
          onChange(newValue, newEvents)

          triggerToast('success', t('common.reportingEventCreateSuccess'))
        }
      } catch (error) {
        const axiosError = error as {
          response?: {
            data?: {
              error?: { code?: string; message?: string }
            }
          }
        }

        const errorCode = axiosError?.response?.data?.error?.code
        const errorMessage =
          errorCode === ERROR_CODES.DUPLICATE
            ? t('common.reportingEventAlreadyExists')
            : t('common.reportingEventCreateError')

        triggerToast('error', errorMessage)
        throw error
      }
    },
    [createMutation, value, selectedEvents, onChange, t]
  )

  return {
    selectedEvents,
    handleSelectEvent,
    handleRemoveEvent,
    handleCreateAndSelect,
    createMutation,
  }
}
